// Helpers shared by the comments and subscribe widgets. esbuild inlines this
// into each bundle, so a page that only wants a subscribe box loads no comment
// code.

/**
 * Where the Worker lives, inferred from this script's own URL so that the
 * blog's templates carry no configuration. Read at load time, while
 * document.currentScript is still meaningful.
 */
export const API_BASE: string = (() => {
  const script = document.currentScript as HTMLScriptElement | null;
  try {
    return script?.src ? new URL(script.src).origin : window.location.origin;
  } catch {
    return window.location.origin;
  }
})();

export interface SeopyoConfig {
  turnstile_sitekey: string;
  google_client_id: string;
  gravatar: boolean;
}

let configPromise: Promise<SeopyoConfig> | null = null;

/** Fetches /config once per page, no matter how many widgets ask. */
export function getConfig(): Promise<SeopyoConfig> {
  configPromise ??= apiGet<SeopyoConfig>("/config").catch(() => ({
    turnstile_sitekey: "",
    google_client_id: "",
    gravatar: false,
  }));
  return configPromise;
}

/** The code is what the widget translates; the prose is the fallback for a
 *  code this bundle predates. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string = "generic",
  ) {
    super(message);
  }
}

async function parse<T>(response: Response): Promise<T> {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    /* fall through to a generic message */
  }

  if (!response.ok) {
    const body = (payload ?? {}) as { error?: unknown; code?: unknown };
    throw new ApiError(
      typeof body.error === "string" ? body.error : "",
      typeof body.code === "string" ? body.code : "generic",
    );
  }
  return payload as T;
}

export async function apiGet<T>(path: string): Promise<T> {
  return parse<T>(await fetch(API_BASE + path, { method: "GET" }));
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  return parse<T>(
    await fetch(API_BASE + path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

type Attrs = Record<string, string | number | boolean | undefined>;
type Child = Node | string | number | null | undefined | false;

/**
 * A child that isn't already a Node becomes a text node: there is deliberately
 * no path through this helper that parses markup, which is the whole XSS
 * defence for untrusted comment bodies and author names.
 */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [name, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue;
    node.setAttribute(name, value === true ? "" : String(value));
  }
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.appendChild(
      child instanceof Node ? child : document.createTextNode(String(child)),
    );
  }
  return node;
}

/** SQLite writes "YYYY-MM-DD HH:MM:SS" in UTC; Safari will not parse that. */
export function parseUtc(value: string): Date {
  return new Date(value.replace(" ", "T") + "Z");
}

/** Widgets pass `locale`, so a Korean widget on an English-locale laptop does
 *  not date its comments "Aug 28, 2026". The moderation page omits it. */
export function formatDate(value: string, locale?: string): string {
  const date = parseUtc(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// --- Google Identity Services ----------------------------------------------

// Declared here because `Window.google` is global and can only be augmented
// once across the browser-side build. Loading the script is each surface's own
// business: the moderation page's shell carries a <script> tag, the widget
// injects one only if a reader is offered the button.

export interface GoogleIdApi {
  initialize(options: {
    client_id: string;
    callback: (response: { credential: string }) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }): void;
  renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
  /** Applies `auto_select`, or shows the One Tap card if it cannot. */
  prompt(): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleIdApi } };
  }
}

// --- Turnstile ------------------------------------------------------------

interface TurnstileApi {
  render(el: HTMLElement, options: Record<string, unknown>): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
  getResponse(widgetId: string): string | undefined;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const TURNSTILE_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let turnstilePromise: Promise<TurnstileApi> | null = null;

/** On demand, not from the page's <head>: a reader who never opens a comment
 *  form should not pay for a third-party script. */
export function loadTurnstile(): Promise<TurnstileApi> {
  turnstilePromise ??= new Promise<TurnstileApi>((resolve, reject) => {
    if (window.turnstile) return resolve(window.turnstile);
    const script = el("script", {
      src: TURNSTILE_SRC,
      async: true,
      defer: true,
    });
    script.onload = () =>
      window.turnstile
        ? resolve(window.turnstile)
        : reject(new Error("Turnstile unavailable"));
    script.onerror = () => reject(new Error("Turnstile failed to load"));
    document.head.appendChild(script);
  });
  return turnstilePromise;
}

export interface Challenge {
  /** The current token, or "" if the challenge has not completed yet. */
  token(): string;
  reset(): void;
  destroy(): void;
}

export async function renderChallenge(
  container: HTMLElement,
  sitekey: string,
  language: string,
): Promise<Challenge> {
  const turnstile = await loadTurnstile();
  const widgetId = turnstile.render(container, {
    sitekey,
    appearance: "interaction-only",
    language,
  });
  return {
    token: () => turnstile.getResponse(widgetId) ?? "",
    reset: () => turnstile.reset(widgetId),
    destroy: () => turnstile.remove(widgetId),
  };
}

/** The check can still be in flight when an eager reader hits submit. */
export async function waitForToken(
  challenge: Challenge,
  timeoutMs = 8000,
): Promise<string> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const token = challenge.token();
    if (token) return token;
    if (Date.now() > deadline) return "";
    await new Promise((resolve) => setTimeout(resolve, 150));
  }
}

// --- Avatars --------------------------------------------------------------

/** Gravatar accepts SHA-256 hashes; `d=mp` gives a neutral silhouette. */
export function gravatarUrl(hash: string, size = 64): string {
  return `https://www.gravatar.com/avatar/${encodeURIComponent(hash)}?s=${size}&d=mp`;
}

/** Deterministic hue from the email hash, for the no-Gravatar fallback. */
export function initialsAvatar(name: string, hash: string): HTMLElement {
  const hue = (parseInt(hash.slice(0, 4) || "0", 16) || 0) % 360;
  const node = el("span", {
    class: "seopyo-avatar seopyo-avatar-initial",
    "aria-hidden": "true",
  });
  node.textContent = (name.trim()[0] ?? "?").toUpperCase();
  node.style.backgroundColor = `hsl(${hue} 45% 45%)`;
  return node;
}
